import { useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Edit2, Settings, Database, Link2, Bell, Shield, ChevronDown, ChevronRight } from "lucide-react";
import { fmtDateTime } from "@/lib/dateFormat";

export default function AdminSettingsPage() {
  const [activeTab, setActiveTab] = useState("config");
  const [configGroups, setConfigGroups] = useState([]);
  const [configItems, setConfigItems] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState("");
  const [integrations, setIntegrations] = useState([]);
  const [gmailStatus, setGmailStatus] = useState({ connected: false, last_sync: null });
  const [notifTemplates, setNotifTemplates] = useState([]);
  const [workflowDefs, setWorkflowDefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [addForm, setAddForm] = useState({ config_group: "", config_key: "", config_value: "", display_order: 0 });
  const [editingItem, setEditingItem] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [editKey, setEditKey] = useState("");
  const [editOrder, setEditOrder] = useState(0);

  const load = async () => {
    try {
      const [groupsRes, intRes, ntRes, wfRes] = await Promise.all([
        api.get("/config/groups"),
        api.get("/integrations").catch(() => ({ data: [] })),
        api.get("/notification-templates").catch(() => ({ data: [] })),
        api.get("/workflow/definitions", { params: { entity_type: "order" } }).catch(() => ({ data: [] })),
      ]);
      setConfigGroups(groupsRes.data || []);
      setIntegrations(Array.isArray(intRes.data) ? intRes.data : []);
      // Load Gmail status — graceful fallback
      try { const gRes = await api.get("/integrations/gmail/status"); setGmailStatus(gRes.data || { connected: false }); } catch { setGmailStatus({ connected: false }); }
      setNotifTemplates(ntRes.data);
      setWorkflowDefs(wfRes.data);
      if (groupsRes.data.length > 0 && !selectedGroup) {
        setSelectedGroup(groupsRes.data[0].group);
      }
    } catch(e) { console.error("Load error:", e); } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (selectedGroup) {
      api.get("/config", { params: { group: selectedGroup, active_only: false } })
        .then(r => setConfigItems(r.data)).catch(() => {});
    }
  }, [selectedGroup]);

  const toggleConfigActive = async (item) => {
    try {
      await api.put(`/config/${item._id}`, { is_active: !item.is_active });
      toast.success(`${item.config_value} ${item.is_active ? 'deactivated' : 'activated'}`);
      const { data } = await api.get("/config", { params: { group: selectedGroup, active_only: false } });
      setConfigItems(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const startEdit = (item) => {
    setEditingItem(item._id);
    setEditValue(item.config_value || "");
    setEditKey(item.config_key || "");
    setEditOrder(item.display_order || 0);
  };

  const saveEdit = async (item) => {
    try {
      await api.put(`/config/${item._id}`, { config_value: editValue, config_key: editKey, display_order: editOrder });
      toast.success("Updated");
      setEditingItem(null);
      const { data } = await api.get("/config", { params: { group: selectedGroup, active_only: false } });
      setConfigItems(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const cancelEdit = () => setEditingItem(null);

  const saveNewConfig = async () => {
    try {
      await api.post("/config", { ...addForm, config_group: addForm.config_group || selectedGroup });
      toast.success("Configuration item added");
      setShowAddDialog(false);
      load();
      if (selectedGroup) {
        const { data } = await api.get("/config", { params: { group: selectedGroup, active_only: false } });
        setConfigItems(data);
      }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (loading) return <p className="text-xs text-zinc-400 text-center py-12">Loading...</p>;

  // Display name overrides for config groups
  const groupDisplayName = (key) => {
    const overrides = { "channel_types": "Trade Format", "customer_types": "Customer Types", "bank_details": "Bank Details", "company_info": "Company Info" };
    return overrides[key] || (key || '').replace(/_/g, ' ');
  };

  return (
    <div data-testid="admin-settings-page">
      <div className="mb-6">
        <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-950">System Settings</h1>
        <p className="text-sm text-zinc-500 mt-1">Configuration engine, workflow definitions, integrations, and notification templates. All database-driven — no code changes needed.</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-zinc-100 rounded-sm h-9">
          <TabsTrigger value="config" className="text-xs rounded-sm"><Database size={12} className="mr-1" /> Configuration</TabsTrigger>
          <TabsTrigger value="workflow" className="text-xs rounded-sm"><Settings size={12} className="mr-1" /> Workflow</TabsTrigger>
          <TabsTrigger value="notifications" className="text-xs rounded-sm"><Bell size={12} className="mr-1" /> Notifications</TabsTrigger>
          <TabsTrigger value="integrations" className="text-xs rounded-sm"><Link2 size={12} className="mr-1" /> Integrations</TabsTrigger>
        </TabsList>

        {/* ═══ CONFIGURATION TAB ═══ */}
        <TabsContent value="config">
          <div className="grid grid-cols-12 gap-4">
            {/* Left: Group list */}
            <div className="col-span-3 border border-zinc-200 rounded-sm">
              <div className="px-3 py-2 bg-zinc-50 border-b border-zinc-200 text-[10px] font-semibold uppercase text-zinc-400">Config Groups</div>
              {configGroups.map(g => (
                <button key={g.group} onClick={() => setSelectedGroup(g.group)}
                  className={`w-full text-left px-3 py-2 text-xs border-b border-zinc-50 flex justify-between ${selectedGroup === g.group ? 'bg-zinc-100 font-semibold text-zinc-900' : 'text-zinc-600 hover:bg-zinc-50'}`}
                  data-testid={`config-group-${g.group}`}>
                  <span>{groupDisplayName(g.group)}</span>
                  <span className="text-zinc-400 font-mono">{g.count}</span>
                </button>
              ))}
            </div>

            {/* Right: Items */}
            <div className="col-span-9">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading text-lg font-semibold text-zinc-950 capitalize">{groupDisplayName(selectedGroup)}</h3>
                <Button onClick={() => { setAddForm({ config_group: selectedGroup, config_key: "", config_value: "", display_order: configItems.length }); setShowAddDialog(true); }} className="rounded-sm h-7 text-xs" data-testid="add-config-btn"><Plus size={12} /> Add Item</Button>
              </div>
              <div className="border border-zinc-200 rounded-sm overflow-hidden">
                <table className="data-table w-full">
                  <thead><tr><th>Key</th><th>Value</th><th className="num w-16">Order</th><th className="w-14">System</th><th className="w-14">Active</th><th className="w-20">Actions</th></tr></thead>
                  <tbody>
                    {configItems.map(item => (
                      <tr key={item._id} className={!item.is_active ? "opacity-40" : ""} data-testid={`config-row-${item.config_key}`}>
                        <td>
                          {editingItem === item._id ? (
                            <Input value={editKey} onChange={e => setEditKey(e.target.value)} className="h-7 text-xs rounded-sm font-mono" />
                          ) : (
                            <span className="font-mono text-xs text-zinc-500">{item.config_key}</span>
                          )}
                        </td>
                        <td>
                          {editingItem === item._id ? (
                            <Input value={editValue} onChange={e => setEditValue(e.target.value)} className="h-7 text-xs rounded-sm" data-testid={`edit-value-${item.config_key}`} autoFocus />
                          ) : (
                            <span className="text-xs font-medium text-zinc-900">{item.config_value || <span className="text-zinc-300 italic">empty</span>}</span>
                          )}
                        </td>
                        <td className="num">
                          {editingItem === item._id ? (
                            <Input type="number" value={editOrder} onChange={e => setEditOrder(parseInt(e.target.value) || 0)} className="h-7 w-14 text-xs rounded-sm text-right" />
                          ) : (
                            <span className="font-mono text-xs">{item.display_order}</span>
                          )}
                        </td>
                        <td className="text-center">{item.is_system ? <Shield size={12} className="text-zinc-400 mx-auto" /> : ""}</td>
                        <td className="text-center"><Switch checked={item.is_active} onCheckedChange={() => toggleConfigActive(item)} className="scale-75" /></td>
                        <td>
                          {editingItem === item._id ? (
                            <div className="flex gap-1">
                              <Button className="rounded-sm h-6 text-[10px] px-2" onClick={() => saveEdit(item)} data-testid={`save-edit-${item.config_key}`}>Save</Button>
                              <Button variant="ghost" className="rounded-sm h-6 text-[10px] px-1" onClick={cancelEdit}>✕</Button>
                            </div>
                          ) : (
                            <button onClick={() => startEdit(item)} className="p-1 hover:bg-zinc-100 rounded-sm" title="Edit value" data-testid={`edit-btn-${item.config_key}`}>
                              <Edit2 size={13} className="text-zinc-400" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* ═══ WORKFLOW TAB ═══ */}
        <TabsContent value="workflow">
          <div>
            <h3 className="font-heading text-lg font-semibold text-zinc-950 mb-3">Order Workflow Definitions</h3>
            <p className="text-xs text-zinc-500 mb-4">Each row defines an allowed status transition and which roles can trigger it. New workflows for future modules (Purchase Orders, Production) are added here — no code changes.</p>
            <div className="border border-zinc-200 overflow-x-auto">
              <table className="data-table w-full">
                <thead><tr><th>From Status</th><th>→</th><th>To Status</th><th>Allowed Roles</th><th>Reason Required</th></tr></thead>
                <tbody>
                  {workflowDefs.map((wf, i) => (
                    <tr key={i}>
                      <td className="text-xs font-medium">{wf.from_status}</td>
                      <td className="text-zinc-400">→</td>
                      <td className="text-xs font-medium">{wf.to_status}</td>
                      <td className="text-[10px] text-zinc-600">{(wf.allowed_roles || []).join(', ')}</td>
                      <td className="text-center">{wf.requires_reason ? <span className="text-red-500 text-xs">Yes</span> : <span className="text-zinc-300">-</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* ═══ NOTIFICATIONS TAB ═══ */}
        <TabsContent value="notifications">
          <div>
            <h3 className="font-heading text-lg font-semibold text-zinc-950 mb-3">Notification Templates</h3>
            <p className="text-xs text-zinc-500 mb-4">Every system event uses a template for its notification. Future channels (WhatsApp, SMS) are added per template.</p>
            <div className="border border-zinc-200 overflow-hidden">
              <table className="data-table w-full">
                <thead><tr><th>Event Key</th><th>Title</th><th>Body Template</th><th>Channels</th></tr></thead>
                <tbody>
                  {notifTemplates.map(nt => (
                    <tr key={nt._id}>
                      <td className="font-mono text-xs text-zinc-500">{nt.event_key}</td>
                      <td className="text-xs font-medium">{nt.title_template}</td>
                      <td className="text-xs text-zinc-600 max-w-[300px] truncate">{nt.body_template}</td>
                      <td><div className="flex gap-1">{(nt.channels || []).map(c => <span key={c} className="px-1.5 py-0.5 text-[9px] bg-zinc-100 text-zinc-600 rounded-sm">{c}</span>)}</div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* ═══ INTEGRATIONS TAB ═══ */}
        <TabsContent value="integrations">
          <div>
            <h3 className="font-heading text-lg font-semibold text-zinc-950 mb-3">Third-Party Integrations</h3>
            <p className="text-xs text-zinc-500 mb-4">Placeholder entries for all planned integrations. API keys and webhooks configured here when activated.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {/* Gmail PO Inbox — Live Integration */}
              <div className="border-2 border-blue-200 bg-white p-4 rounded-sm" data-testid="gmail-integration-card">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h4 className="text-sm font-semibold text-zinc-900">Gmail PO Inbox</h4>
                    <p className="text-[10px] text-zinc-500">Monitors po@skippi.in for incoming Qcom POs</p>
                  </div>
                  <span className={`px-2 py-0.5 text-[9px] font-semibold rounded-sm ${gmailStatus?.connected ? 'bg-green-100 text-green-800' : 'bg-zinc-200 text-zinc-600'}`}>{gmailStatus?.connected ? 'connected' : 'not connected'}</span>
                </div>
                {gmailStatus?.connected ? (
                  <div className="space-y-2">
                    <p className="text-[10px] text-zinc-600">Account: <span className="font-mono font-semibold">orbit-sync@skippi.in</span></p>
                    {gmailStatus.last_sync && <p className="text-[10px] text-zinc-400">Last sync: {fmtDateTime(gmailStatus.last_sync)}</p>}
                    <div className="flex gap-2">
                      <Button className="rounded-sm h-7 text-[10px] px-3" onClick={async () => {
                        try { const { data } = await api.post("/integrations/gmail/sync"); toast.success(`Synced: ${data.new_emails || 0} new emails from ${data.total_checked || 0} checked (start: ${data.effective_start || '?'})`); api.get("/integrations/gmail/status").then(r => setGmailStatus(r.data || {})); } catch (err) { toast.error(err.response?.data?.message || "Sync failed"); }
                      }} data-testid="gmail-sync-btn">Sync Now</Button>
                      <Button variant="outline" className="rounded-sm h-7 text-[10px] px-3 border-red-300 text-red-600" onClick={async () => {
                        if (!window.confirm("Disconnect Gmail? PO sync will stop.")) return;
                        try { await api.post("/integrations/gmail/disconnect"); setGmailStatus({ connected: false }); toast.success("Gmail disconnected"); } catch(e) { console.error("Error:", e); }
                      }}>Disconnect</Button>
                    </div>
                  </div>
                ) : (
                  <Button className="rounded-sm h-7 text-[10px] px-3 mt-2 bg-blue-600 hover:bg-blue-700" onClick={async () => {
                    try { const { data } = await api.get("/integrations/gmail/auth-url"); window.open(data.auth_url, "_blank"); } catch { toast.error("Failed to get auth URL"); }
                  }} data-testid="gmail-connect-btn">Connect Gmail</Button>
                )}
              </div>

              {/* Existing planned integrations */}
              {(integrations || []).filter(int_ => int_ && int_.integration_name).map((int_, idx) => (
                <div key={int_._id || idx} className="border border-zinc-200 p-4" data-testid={`integration-${(int_.integration_name || 'unknown').toLowerCase().replace(/\s+/g,'-')}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h4 className="text-sm font-semibold text-zinc-900">{int_.integration_name}</h4>
                      <p className="text-[10px] text-zinc-500">{int_.description}</p>
                    </div>
                    <span className={`px-2 py-0.5 text-[9px] font-semibold rounded-sm ${int_.status === 'active' ? 'bg-green-100 text-green-800' : int_.status === 'planned' ? 'bg-blue-100 text-blue-800' : 'bg-zinc-100 text-zinc-600'}`}>{int_.status}</span>
                  </div>
                  {int_.api_key && <p className="text-[10px] font-mono text-zinc-400 truncate">Key: {int_.api_key.substring(0, 20)}...</p>}
                  {int_.last_synced_at && <p className="text-[10px] text-zinc-400">Last sync: {fmtDateTime(int_.last_synced_at)}</p>}
                </div>
              ))}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Add Config Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">Add Configuration Item</DialogTitle>
            <DialogDescription>Adding to group: {groupDisplayName(addForm.config_group)}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Key (internal)</Label><Input value={addForm.config_key} onChange={(e) => setAddForm({...addForm, config_key: e.target.value.replace(/\s+/g, '_')})} className="mt-1 rounded-sm font-mono" /></div>
            <div><Label className="text-xs">Display Value</Label><Input value={addForm.config_value} onChange={(e) => setAddForm({...addForm, config_value: e.target.value})} className="mt-1 rounded-sm" data-testid="config-value-input" /></div>
            <div><Label className="text-xs">Display Order</Label><Input type="number" value={addForm.display_order} onChange={(e) => setAddForm({...addForm, display_order: parseInt(e.target.value) || 0})} className="mt-1 rounded-sm" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)} className="rounded-sm text-xs">Cancel</Button>
            <Button onClick={saveNewConfig} className="rounded-sm text-xs" data-testid="save-config-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
